import React from 'react';
import { Typography, Grid } from '@mui/joy';
import { Card, Button } from '@components/ui';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const stats = [
  {
    title: 'Security Score',
    value: '85%',
    change: '+5%',
    icon: ShieldCheckIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  {
    title: 'Active Threats',
    value: '3',
    change: '-2',
    icon: ExclamationTriangleIcon,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  {
    title: 'Compliance Status',
    value: '92%',
    change: '+1%',
    icon: CheckCircleIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  {
    title: 'Last Scan',
    value: '2h ago',
    change: 'Scheduled',
    icon: ClockIcon,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
];

const recentScans = [
  { id: 1, target: 'api.fortifytech.com', status: 'completed', vulnerabilities: 2, date: '2024-01-10' },
  { id: 2, target: 'admin.fortifytech.com', status: 'in-progress', vulnerabilities: 0, date: '2024-01-10' },
  { id: 3, target: 'db.fortifytech.com', status: 'completed', vulnerabilities: 5, date: '2024-01-09' },
];

export const Dashboard: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Typography level="h2" className="font-bold text-gray-900 dark:text-white">
          Security Dashboard
        </Typography>
        <Typography level="body-md" className="text-gray-600 dark:text-gray-400 mt-1">
          Monitor your organization&apos;s security posture in real-time
        </Typography>
      </div>

      {/* Stats Grid */}
      <Grid container spacing={3} className="mb-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Grid key={stat.title} xs={12} sm={6} md={3}>
              <Card className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography level="body-sm" className="text-gray-600 dark:text-gray-400">
                      {stat.title}
                    </Typography>
                    <Typography level="h3" className="font-bold mt-1">
                      {stat.value}
                    </Typography>
                    <Typography level="body-xs" className={cn('mt-1', stat.color)}>
                      {stat.change}
                    </Typography>
                  </div>
                  <div className={cn('p-3 rounded-lg', stat.bgColor)}>
                    <Icon className={cn('w-6 h-6', stat.color)} />
                  </div>
                </div>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Recent Scans */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Typography level="h4" className="font-semibold">
            Recent Security Scans
          </Typography>
          <Button size="sm" variant="soft">
            View All
          </Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-sm text-gray-700 dark:text-gray-300">
                  Target
                </th>
                <th className="text-left py-3 px-4 font-medium text-sm text-gray-700 dark:text-gray-300">
                  Status
                </th>
                <th className="text-left py-3 px-4 font-medium text-sm text-gray-700 dark:text-gray-300">
                  Vulnerabilities
                </th>
                <th className="text-left py-3 px-4 font-medium text-sm text-gray-700 dark:text-gray-300">
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {recentScans.map((scan) => (
                <tr key={scan.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-3 px-4 text-sm">{scan.target}</td>
                  <td className="py-3 px-4">
                    <span
                      className={cn(
                        'inline-flex px-2 py-1 text-xs font-medium rounded-full',
                        scan.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      )}
                    >
                      {scan.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    <span
                      className={cn(
                        'font-medium',
                        scan.vulnerabilities > 0 ? 'text-red-600' : 'text-green-600'
                      )}
                    >
                      {scan.vulnerabilities}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                    {scan.date}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Quick Actions */}
      <Grid container spacing={3}>
        <Grid xs={12} md={6}>
          <Card className="p-6">
            <Typography level="h4" className="font-semibold mb-4">
              Quick Actions
            </Typography>
            <div className="space-y-3">
              <Button fullWidth variant="solid" color="primary">
                Run Security Audit
              </Button>
              <Button fullWidth variant="outlined" color="neutral">
                Generate Compliance Report
              </Button>
              <Button fullWidth variant="soft" color="warning">
                Schedule Vulnerability Scan
              </Button>
            </div>
          </Card>
        </Grid>
        
        <Grid xs={12} md={6}>
          <Card className="p-6">
            <Typography level="h4" className="font-semibold mb-4">
              Threat Intelligence Feed
            </Typography>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-red-500 mt-2" />
                <div>
                  <Typography level="body-sm" className="font-medium">
                    Critical: CVE-2024-0001 detected
                  </Typography>
                  <Typography level="body-xs" className="text-gray-500">
                    2 hours ago
                  </Typography>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2" />
                <div>
                  <Typography level="body-sm" className="font-medium">
                    Warning: Unusual login patterns detected
                  </Typography>
                  <Typography level="body-xs" className="text-gray-500">
                    5 hours ago
                  </Typography>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                <div>
                  <Typography level="body-sm" className="font-medium">
                    Info: Security patch available
                  </Typography>
                  <Typography level="body-xs" className="text-gray-500">
                    1 day ago
                  </Typography>
                </div>
              </div>
            </div>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

function cn(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}